import { css } from '@emotion/css'
import { saveAs } from 'file-saver'
import cookies from 'js-cookie'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { decorateFetch } from '../../../api'
import { Dialog, DialogFooter } from '../../../legacy-design-system/modal'
import { useOrganizationFeature } from '../../organization-feature'
import { Radio } from '@contrast-security-inc/design-system-inputs'
import { logError } from '../../toaster'
import { reportsMenuItemStyle } from '../index'
import { Button } from '@contrast-security-inc/design-system-buttons'
import {
  Modal,
  ModalFooter
} from '@contrast-security-inc/design-system-popovers'
import { SbomFormat, SbomType } from './sbom-enums'

interface FeatureGuardedSbomReportMenuItemProps {
  organizationID: string
  onClick: () => void
}

interface SbomReportDownloadProps {
  organizationID: string
  applicationID: string
  selectedFormat: string
  closeModal?: () => void
  sbomType: SbomType
}

export const buildDownloadURL = ({
  organizationID,
  applicationID,
  selectedFormat,
  sbomType
}: SbomReportDownloadProps) => {
  return sbomType === SbomType.Server
    ? `/Contrast/api/ng/${organizationID}/servers/${applicationID}/libraries/sbom/${selectedFormat}`
    : `/Contrast/api/ng/${organizationID}/applications/${applicationID}/libraries/sbom/${selectedFormat}`
}

export const generateSbom = async ({
  organizationID,
  applicationID,
  selectedFormat,
  sbomType,
  closeModal
}: SbomReportDownloadProps) => {
  const decoratedFetch = decorateFetch(
    fetch,
    cookies,
    document.location,
    window.localStorage
  )

  let res: Response
  try {
    res = await decoratedFetch(
      buildDownloadURL({
        organizationID,
        applicationID,
        selectedFormat,
        sbomType
      }),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
  } catch (e) {
    return logError('Failed to download file')
  }

  if (res) {
    if (!res.ok || res.status >= 400) {
      return logError('Failed to download file')
    }

    let blob: Blob
    try {
      blob = await res.blob()
    } catch (e) {
      return logError('Failed to download file')
    }

    const filename = 'sbom-report'

    try {
      saveAs(blob, filename)
    } catch (e) {
      return logError('Failed to download file')
    }

    if (closeModal) {
      closeModal()
    }
  }
}

export function FeatureGuardedSbomReportMenuItem({
  organizationID,
  onClick
}: FeatureGuardedSbomReportMenuItemProps) {
  const { isLoading, enabled } = useOrganizationFeature(
    organizationID,
    'oss-inventory-mode'
  )

  if (isLoading || !enabled) {
    return null
  }

  return <SbomReportMenuItem onClick={onClick} />
}

interface SbomModalServerProps {
  isOpen: boolean
  closeModal: () => void
  organizationID: string
  applicationID: string
  sbomType: SbomType
}

interface SbomModalProps {
  isOpen: boolean
  closeModal: () => void
  organizationID: string
  applicationID: string
  sbomType: SbomType
}

export function SbomServerModal({
  isOpen,
  closeModal,
  organizationID,
  applicationID,
  sbomType
}: SbomModalServerProps) {
  const [selectedFormat, setSelectedFormat] = useState(SbomFormat.Cyclone)
  const intl = useIntl()

  return (
    <Modal
      isOpen={isOpen}
      testId="SbomServerModal"
      onClose={closeModal}
      size="md"
      title={<FormattedMessage id="title_applicationDashboard_SBOM_dialog" />}
      variant="info"
    >
      <div
        className={css`
          display: flex;
          padding: 10px;
          gap: 50px;
          margin-top: 30px;
          margin-left: 10px;
        `}
      >
        <SbomOptions
          selectedFormat={selectedFormat}
          setSelectedFormat={setSelectedFormat}
        />
      </div>
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={closeModal}
          data-testid={'cancel-button'}
        >
          {intl.formatMessage({ id: 'action_cancel' })}
        </Button>
        <Button
          onClick={() =>
            generateSbom({
              organizationID,
              applicationID,
              selectedFormat,
              closeModal,
              sbomType
            })
          }
        >
          {intl.formatMessage({ id: 'action_applicationReports_generate' })}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export function SbomModal({
  isOpen,
  closeModal,
  organizationID,
  applicationID,
  sbomType
}: SbomModalProps) {
  const [selectedFormat, setSelectedFormat] = useState(SbomFormat.Cyclone)

  return (
    <Dialog
      isOpen={isOpen}
      testId="SbomModal"
      onCancel={closeModal}
      renderTitle={() => (
        <FormattedMessage id="title_applicationDashboard_SBOM_dialog" />
      )}
      size="small"
    >
      <div
        className={css`
          display: flex;
          padding: 10px;
          gap: 12px;
          margin-top: 10px;
          margin-left: 10px;
        `}
      >
        <SbomOptions
          selectedFormat={selectedFormat}
          setSelectedFormat={setSelectedFormat}
        />
      </div>
      <DialogFooter>
        <FormattedMessage id="action_cancel">
          {(text) => (
            <Button
              variant="secondary"
              className={css`
                height: 34px;
                font-size: 14px;
              `}
              onClick={closeModal}
            >
              {text}
            </Button>
          )}
        </FormattedMessage>

        <FormattedMessage
          id="action_applicationReports_generate"
          children={(message) => (
            <Button
              variant="primary"
              id={'generate_' + selectedFormat + '_report_button'}
              className={css`
                margin-left: 10px;
                height: 34px;
                font-size: 14px;
              `}
              onClick={() =>
                generateSbom({
                  organizationID,
                  applicationID,
                  selectedFormat,
                  closeModal,
                  sbomType
                })
              }
              autoFocus
            >
              {message}
            </Button>
          )}
        />
      </DialogFooter>
    </Dialog>
  )
}

export function SbomOptions({ selectedFormat, setSelectedFormat }) {
  return (
    <>
      <Radio
        testId="cycloneDX_radio_button"
        label={'Cyclone DX'}
        isChecked={selectedFormat === SbomFormat.Cyclone}
        isDisabled={false}
        onChange={() => {
          setSelectedFormat(SbomFormat.Cyclone)
        }}
      />
      <Radio
        testId="SPDX_radio_button"
        label={'SPDX'}
        isChecked={selectedFormat === SbomFormat.SPDX}
        isDisabled={false}
        onChange={() => {
          setSelectedFormat(SbomFormat.SPDX)
        }}
      />
      <Radio
        testId="SPDXV3_radio_button"
        label={'SPDX_V3'}
        isChecked={selectedFormat === SbomFormat.SPDXV3}
        onChange={() => {
          setSelectedFormat(SbomFormat.SPDXV3)
        }}
      />
    </>
  )
}
interface SbomReportMenuItemProps {
  onClick: () => void
}

export function SbomReportMenuItem({ onClick }: SbomReportMenuItemProps) {
  return (
    <React.Fragment>
      <li
        data-testid="generateSbomClick"
        onClick={onClick}
        className={reportsMenuItemStyle}
      >
        <FormattedMessage id="title_applicationDashboard_SBOM">
          {(title) => {
            return <span>{title}</span>
          }}
        </FormattedMessage>
      </li>
    </React.Fragment>
  )
}
